import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, MoreHorizontal, Plus, Info, Leaf, Dumbbell, BookOpen, Briefcase, Palette, Repeat, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TZ } from "@/pages/Oggi";

const FREQ: [string, string][] = [["daily", "Giornalieri"], ["weekly", "Settimanali"], ["monthly", "Mensili"], ["once", "Singoli"]];
const CAT_LABEL: Record<string, string> = { benessere: "Benessere", sport: "Sport", studio: "Studio", lavoro: "Lavoro", creativita: "Creatività", abitudini: "Abitudini", relazioni: "Relazioni", altro: "Altro" };
const FREQ_ONE: Record<string, string> = { daily: "Ogni giorno", weekly: "Ogni settimana", monthly: "Ogni mese", once: "Una volta" };

function ScoreCard() {
  const [rules, setRules] = useState(false);
  const { data: s } = useQuery({ queryKey: ["score"], queryFn: () => api.get("/score", { params: { tz: TZ } }).then((r) => r.data) });
  if (!s) return <div className="surface p-6"><Spinner /></div>;
  const pct = s.next_level ? Math.min(100, Math.round((s.points / s.next_level.at) * 100)) : 100;
  return (
    <section className="surface p-5 sm:p-6 space-y-4" data-testid="score-card">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label text-ink/55">Il tuo percorso</div>
          <div className="text-3xl font-semibold tracking-tight mt-1"><span data-testid="score-points">{s.points}</span> <span className="text-base font-medium text-ink/60">punti</span></div>
        </div>
        <span className="chip chip-violet" data-testid="score-level">{s.level}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/60 overflow-hidden"><div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${pct}%` }} /></div>
      <div className="flex justify-between text-sm text-ink/66">
        <span>{s.this_week} {s.this_week === 1 ? "completamento" : "completamenti"} questa settimana</span>
        {s.next_level && <span>{s.next_level.at - s.points} punti a {s.next_level.name}</span>}
      </div>
      <button onClick={() => setRules((r) => !r)} className="text-sm text-ink/70 inline-flex items-center gap-1.5 min-h-[44px]" data-testid="score-rules-toggle"><Info size={14} />Come funziona il punteggio</button>
      {rules && <ul className="text-sm text-ink/70 space-y-1 list-disc pl-5" data-testid="score-rules">{s.rules.map((r: string) => <li key={r}>{r}</li>)}</ul>}
    </section>
  );
}

function GoalForm({ goal, freq, onDone }: any) {
  const [f, setF] = useState({ title: goal?.title || "", description: goal?.description || "", frequency: goal?.frequency || freq, due_date: goal?.due_date || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body: any = { title: f.title, description: f.description, frequency: f.frequency, due_date: f.due_date || null };
      if (goal) await api.patch(`/goals/${goal.id}`, { ...body, clear_due_date: !f.due_date }, { params: { tz: TZ } });
      else await api.post("/goals", body, { params: { tz: TZ } });
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="goal-form">
      <input className="field" required maxLength={80} placeholder="Es. Camminare 20 minuti" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="goal-title-input" />
      <textarea className="field min-h-[80px]" maxLength={300} placeholder="Descrizione (facoltativa)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="goal-description-input" />
      <div className="seg flex-wrap" role="radiogroup">
        {FREQ.map(([k, l]) => <button type="button" key={k} role="radio" aria-checked={f.frequency === k} onClick={() => setF({ ...f, frequency: k })} className={`seg-item ${f.frequency === k ? "seg-active" : ""}`} data-testid={`goal-freq-${k}`}>{FREQ_ONE[k]}</button>)}
      </div>
      <label className="block text-sm text-ink/70">Scadenza (facoltativa)<input type="date" className="field mt-1.5" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="goal-due-input" /></label>
      <button className="btn btn-primary w-full" disabled={busy || !f.title.trim()} data-testid="goal-save-btn">{goal ? "Salva modifiche" : "Crea obiettivo"}</button>
    </form>
  );
}

const CATS: [string, string, any][] = [["benessere", "Benessere", Leaf], ["sport", "Sport", Dumbbell], ["studio", "Studio", BookOpen], ["lavoro", "Lavoro", Briefcase], ["creativita", "Creatività", Palette], ["abitudini", "Abitudini", Repeat], ["relazioni", "Relazioni", Heart], ["altro", "Altro", Sparkles]];
const IDEAS: Record<string, string[]> = {
  benessere: ["Dedicare 10 minuti a me stesso", "Bere 2 litri d'acqua", "Dormire 8 ore", "Fare 5 minuti di respirazione"],
  sport: ["Camminare 20 minuti", "Fare stretching 10 minuti", "Correre 3 km", "Salire le scale invece dell'ascensore"],
  studio: ["Leggere 10 pagine", "Studiare 30 minuti", "Ripassare gli appunti", "Imparare 5 parole nuove"],
  lavoro: ["Finire il compito più importante", "Fare una pausa ogni 90 minuti", "Aggiornare il curriculum", "Chiudere la posta entro le 18"],
  creativita: ["Scrivere una canzone", "Disegnare per 15 minuti", "Scattare una foto al giorno", "Scrivere una pagina"],
  abitudini: ["Niente telefono la prima ora", "Mettere in ordine la scrivania", "Preparare il pranzo a casa", "Andare a letto entro le 23"],
  relazioni: ["Chiamare una persona cara", "Scrivere a un amico", "Cenare senza telefono", "Dire grazie a qualcuno"],
  altro: [],
};
const FREQ_HINT: Record<string, string> = { daily: "Lo segni una volta al giorno", weekly: "Una volta a settimana", monthly: "Una volta al mese", once: "Un traguardo da raggiungere una volta" };
const OPTION = "w-full text-left rounded-2xl bg-white/55 border border-white/80 hover:bg-white/80";
const PICKED = "shadow-[inset_0_0_0_2px_hsl(var(--primary)/.5)]";

function Steps({ n }: { n: number }) {
  return <div className="flex gap-1.5" aria-label={`Passo ${n} di 4`} data-testid="wizard-steps">{[1, 2, 3, 4].map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= n ? "bg-[hsl(var(--primary))]" : "bg-white/60"}`} />)}</div>;
}

function StepCategory({ g, next }: any) {
  return (<>
    <h3 className="font-semibold text-lg">In quale ambito vuoi crescere?</h3>
    <div className="grid grid-cols-2 gap-2">{CATS.map(([k, l, I]) => <button key={k} onClick={() => next({ category: k })} className={`${OPTION} flex items-center gap-3 min-h-[52px] px-4 ${g.category === k ? PICKED : ""}`} data-testid={`wizard-cat-${k}`}><I size={18} className="text-[hsl(var(--primary))]" /><span className="font-medium">{l}</span></button>)}</div>
  </>);
}

function StepWhat({ g, setG }: any) {
  return (<>
    <h3 className="font-semibold text-lg">Cosa vuoi raggiungere?</h3>
    {IDEAS[g.category]?.length > 0 && <div className="flex flex-wrap gap-2">{IDEAS[g.category].map((t) => <button key={t} onClick={() => setG({ ...g, title: t })} aria-pressed={g.title === t} className={`chip min-h-[40px] px-4 text-sm ${g.title === t ? "chip-violet" : "hover:bg-white/70"}`} data-testid="wizard-idea">{t}</button>)}</div>}
    <input className="field" maxLength={80} placeholder="Oppure scrivi il tuo obiettivo" value={g.title} onChange={(e) => setG({ ...g, title: e.target.value })} data-testid="wizard-title-input" />
  </>);
}

function StepFreq({ g, next }: any) {
  return (<>
    <h3 className="font-semibold text-lg">Ogni quanto?</h3>
    <div className="space-y-2">{FREQ.map(([k]) => <button key={k} onClick={() => next({ frequency: k })} className={`${OPTION} px-4 py-3 ${g.frequency === k ? PICKED : ""}`} data-testid={`wizard-freq-${k}`}><span className="block font-medium">{k === "once" ? "Obiettivo singolo" : FREQ_ONE[k]}</span><span className="block text-sm text-ink/62">{FREQ_HINT[k]}</span></button>)}</div>
  </>);
}

function StepDetails({ g, setG }: any) {
  return (<>
    <h3 className="font-semibold text-lg">Ultimi dettagli</h3>
    <input className="field" maxLength={80} value={g.title} onChange={(e) => setG({ ...g, title: e.target.value })} data-testid="goal-title-input" />
    <textarea className="field min-h-[72px]" maxLength={300} placeholder="Perché è importante per te? Come lo farai? (facoltativo)" value={g.description} onChange={(e) => setG({ ...g, description: e.target.value })} data-testid="goal-description-input" />
    <label className="block text-sm text-ink/70">{g.frequency === "once" ? "Entro quando? (facoltativo)" : "Fino a quando? (facoltativo)"}<input type="date" className="field mt-1.5 [color-scheme:light]" value={g.due_date} onChange={(e) => setG({ ...g, due_date: e.target.value })} data-testid="goal-due-input" /></label>
    <p className="text-sm text-ink/68" data-testid="wizard-summary">{CAT_LABEL[g.category]} · {g.frequency === "once" ? "Obiettivo singolo" : FREQ_ONE[g.frequency]} · +10 punti ogni volta che lo completi</p>
  </>);
}

function GoalWizard({ freq, onDone }: any) {
  const [step, setStep] = useState(1);
  const [g, setG] = useState({ category: "", title: "", frequency: freq, description: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/goals", { ...g, due_date: g.due_date || null }, { params: { tz: TZ } }); toast.success("Obiettivo creato"); onDone(g.frequency); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const next = (patch: any = {}) => { setG((x: any) => ({ ...x, ...patch })); setStep((s) => s + 1); };
  return (
    <div className="space-y-5" data-testid="goal-wizard">
      <Steps n={step} />
      {step === 1 && <StepCategory g={g} next={next} />}
      {step === 2 && <StepWhat g={g} setG={setG} />}
      {step === 3 && <StepFreq g={g} next={next} />}
      {step === 4 && <StepDetails g={g} setG={setG} />}
      <div className="flex gap-2 justify-between">
        {step > 1 ? <button className="btn btn-ghost" onClick={() => setStep((s) => s - 1)} data-testid="wizard-back-btn">Indietro</button> : <span />}
        {step === 2 && <button className="btn btn-primary" disabled={!g.title.trim()} onClick={() => next()} data-testid="wizard-next-btn">Avanti</button>}
        {step === 4 && <button className="btn btn-primary" disabled={busy || !g.title.trim()} onClick={save} data-testid="goal-save-btn">Crea obiettivo</button>}
      </div>
    </div>
  );
}

function Milestone({ n }: { n: number }) {
  const m = n >= 30 ? "30 volte" : n >= 7 ? "7 volte" : n >= 1 ? "Primo passo" : null;
  return m ? <span className="chip chip-lime text-[11px] py-0.5" data-testid="goal-milestone">{m}</span> : null;
}

function GoalRow({ g, onToggle, onEdit, onArchive, onDelete }: any) {
  return (
    <div className="row" data-testid="goal-item">
      <button onClick={onToggle} aria-label={g.done_current ? "Segna come non completato" : "Segna come completato"} aria-pressed={g.done_current} data-testid="goal-toggle-btn"
        className={`w-11 h-11 -m-2 rounded-full flex items-center justify-center shrink-0`}>
        <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${g.done_current ? "bg-[hsl(var(--primary))] border-transparent text-white" : "border-ink/30 bg-white/50"}`}>{g.done_current && <Check size={14} strokeWidth={3} />}</span>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><span className={`font-semibold ${g.done_current ? "line-through text-ink/55" : ""}`} data-testid="goal-title">{g.title}</span><Milestone n={g.completions} /></div>
        <div className="text-xs text-ink/60 mt-0.5">{CAT_LABEL[g.category] ? `${CAT_LABEL[g.category]} · ` : ""}{FREQ_ONE[g.frequency]}{g.due_date ? ` · entro il ${new Date(g.due_date).toLocaleDateString("it-IT")}` : ""} · {g.completions} {g.completions === 1 ? "volta" : "volte"} · {g.completions * 10} punti</div>
        {g.description && <p className="text-sm text-ink/70 mt-1">{g.description}</p>}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button className="icon-btn" aria-label="Opzioni obiettivo" data-testid="goal-menu-btn"><MoreHorizontal size={18} /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="menu-panel">
          <DropdownMenuItem onClick={onEdit} data-testid="goal-edit-btn">Modifica</DropdownMenuItem>
          <DropdownMenuItem onClick={onArchive} data-testid="goal-archive-btn">{g.archived ? "Riattiva" : "Archivia"}</DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} className="text-[hsl(var(--destructive))]" data-testid="goal-delete-btn">Elimina</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function History() {
  const { data } = useQuery({ queryKey: ["goal-history"], queryFn: () => api.get("/goals/history").then((r) => r.data) });
  if (!data) return <Spinner />;
  if (!data.length) return <Empty testId="history-empty">Quando completi un obiettivo, lo ritrovi qui.</Empty>;
  return (
    <div className="surface p-1" data-testid="goal-history">
      {data.map((h: any) => <div key={h.id} className="row text-sm"><Check size={15} className="text-[hsl(var(--primary))]" /><span className="flex-1">{h.title}</span><span className="text-ink/60">{new Date(h.at).toLocaleDateString("it-IT")} · +{h.points}</span></div>)}
    </div>
  );
}

export default function Cresci() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("daily");
  const [edit, setEdit] = useState<any>(null);
  const [toDelete, setToDelete] = useState<any>(null);
  const archived = tab === "archived";
  const { data: goals, isLoading } = useQuery({ queryKey: ["goals", archived], queryFn: () => api.get("/goals", { params: { tz: TZ, archived } }).then((r) => r.data) });
  const refresh = () => ["goals", "score", "goal-history"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const act = async (p: Promise<any>) => { try { await p; refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const list = archived ? goals || [] : (goals || []).filter((g: any) => g.frequency === tab);
  return (
    <div data-testid="cresci-page">
      <PageHeader kicker="Obiettivi personali" title="Cresci" right={<button className="btn btn-primary" onClick={() => setEdit({})} data-testid="new-goal-btn"><Plus size={17} />Nuovo</button>} />
      <div className="px-5 md:px-10 max-w-3xl space-y-6 pb-12">
        <ScoreCard />
        <div className="seg grid grid-cols-3 sm:flex sm:w-fit rounded-[1.4rem] sm:rounded-full" role="tablist">
          {[...FREQ, ["history", "Storico"], ["archived", "Archiviati"]].map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`seg-item justify-center ${tab === k ? "seg-active" : ""}`} data-testid={`goals-tab-${k}`}>{l}</button>)}
        </div>
        {tab === "history" ? <History /> : isLoading ? <Spinner /> : !list.length ? (
          <Empty testId="goals-empty">{archived ? "Nessun obiettivo archiviato." : "Nessun obiettivo qui. Inizia da qualcosa di piccolo e realistico."}</Empty>
        ) : (
          <div className="surface p-1" data-testid="goals-list">
            {list.map((g: any) => <GoalRow key={g.id} g={g}
              onToggle={() => act(g.done_current ? api.delete(`/goals/${g.id}/complete`, { params: { tz: TZ } }) : api.post(`/goals/${g.id}/complete`, null, { params: { tz: TZ } }))}
              onEdit={() => setEdit(g)} onArchive={() => act(api.patch(`/goals/${g.id}`, { archived: !g.archived }))}
              onDelete={() => setToDelete(g)} />)}
          </div>
        )}
        <p className="text-xs text-ink/55">Il punteggio è privato e non prevede classifiche né penalità. Il promemoria serale si attiva da Impostazioni.</p>
      </div>
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="menu-panel rounded-3xl max-w-sm w-[calc(100%-2rem)]" data-testid="goal-delete-dialog">
          <DialogHeader><DialogTitle>Eliminare questo obiettivo?</DialogTitle></DialogHeader>
          <p className="text-sm text-ink/72">"{toDelete?.title}" verrà rimosso. I punti già ottenuti restano nello storico.</p>
          <div className="flex gap-2 justify-end">
            <button className="btn btn-ghost" onClick={() => setToDelete(null)} data-testid="goal-delete-cancel-btn">Annulla</button>
            <button className="btn btn-danger" onClick={() => { act(api.delete(`/goals/${toDelete.id}`)); setToDelete(null); }} data-testid="goal-delete-confirm-btn">Elimina</button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="menu-panel rounded-3xl max-w-md w-[calc(100%-2rem)] max-h-[85dvh] overflow-y-auto" data-testid="goal-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica obiettivo" : "Nuovo obiettivo"}</DialogTitle></DialogHeader>
          {edit && (edit.id ? <GoalForm goal={edit} freq={edit.frequency} onDone={() => { setEdit(null); refresh(); }} />
            : <GoalWizard freq={FREQ.some(([k]) => k === tab) ? tab : "daily"} onDone={(f: string) => { setEdit(null); setTab(f); refresh(); }} />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
